import { Link, useRouterState } from '@tanstack/react-router';
import { Menu, RefreshCw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { ThemeToggle } from '@/components/ThemeToggle';
import { NotificationToggle } from '@/components/NotificationToggle';
import { Footer } from '@/components/Footer';
import { navItems } from '@/lib/nav';
import { cn } from '@/lib/utils';

export function MobileShell({ children }: { children: React.ReactNode }) {
  const { location } = useRouterState();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries();
  const current = navItems.find((n) => n.to === location.pathname) ?? navItems[0];

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="sticky top-0 z-30 flex h-12 items-center gap-2 border-b bg-background/90 px-3 backdrop-blur">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Menu"><Menu /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <SheetHeader className="border-b p-4 text-left">
              <SheetTitle className="flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground text-sm font-black">M</span>
                MAESTRO
              </SheetTitle>
            </SheetHeader>
            <nav className="flex flex-col p-2">
              {navItems.map((it) => {
                const Icon = it.icon;
                const active = location.pathname === it.to;
                return (
                  <SheetClose asChild key={it.to}>
                    <Link
                      to={it.to}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                        active ? 'bg-secondary text-secondary-foreground' : 'hover:bg-accent'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {it.label}
                    </Link>
                  </SheetClose>
                );
              })}
            </nav>
          </SheetContent>
        </Sheet>
        <h1 className="flex-1 text-sm font-bold uppercase tracking-wider">{current.label}</h1>
        <Button variant="ghost" size="icon" onClick={refresh} aria-label="Rafraîchir"><RefreshCw /></Button>
        <NotificationToggle />
        <ThemeToggle />
      </header>

      <main className="flex-1 overflow-x-hidden px-3 py-3 pb-20">{children}</main>

      <nav className="fixed inset-x-0 bottom-0 z-30 flex h-16 items-center justify-around border-t bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur">
        {navItems.map((it) => {
          const Icon = it.icon;
          const active = location.pathname === it.to;
          return (
            <Link
              key={it.to}
              to={it.to}
              className={cn(
                'flex h-full flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors',
                active ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              <Icon className={cn('h-5 w-5', active && 'scale-110')} />
              <span>{it.shortLabel ?? it.label}</span>
            </Link>
          );
        })}
      </nav>

      <Footer />
    </div>
  );
}
